const LIVE_BOTTOM_THRESHOLD = 36;
const LIVE_ITEM_ANIMATION_MS = 300;
function resolveStudioInfoTab(root, preferredTab = '') {
    const panels = Array.from(root.querySelectorAll('[data-studio-panel]'));
    if (panels.length === 0) return '';

    if (preferredTab && panels.some((panel) => panel.dataset.studioPanel === preferredTab)) {
        return preferredTab;
    }

    const firstVisibleTab = root.querySelector('[data-studio-tab]')?.dataset.studioTab || '';
    if (firstVisibleTab && panels.some((panel) => panel.dataset.studioPanel === firstVisibleTab)) {
        return firstVisibleTab;
    }

    return panels[0].dataset.studioPanel || '';
}

function applyStudioInfoTabToRoot(root, preferredTab = '') {
    const activeTab = resolveStudioInfoTab(root, preferredTab);
    root.querySelectorAll('[data-studio-panel]').forEach((panel) => {
        panel.classList.toggle('hidden', activeTab !== '' && panel.dataset.studioPanel !== activeTab);
    });
    root.querySelectorAll('[data-studio-tab]').forEach((button) => {
        const active = button.dataset.studioTab === activeTab;
        button.classList.toggle('bg-sky-500/15', active);
        button.classList.toggle('text-sky-200', active);
        button.classList.toggle('ring-1', active);
        button.classList.toggle('ring-sky-400/25', active);
        button.classList.toggle('text-slate-400', !active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    return activeTab;
}

function applyStudioInfoTab(center) {
    const activeTab = applyStudioInfoTabToRoot(center, center.dataset.studioInfoTab || '');
    if (activeTab) center.dataset.studioInfoTab = activeTab;
}

function applyStudioLineupCollapsed(root, collapsed) {
    root.querySelectorAll('[data-studio-lineup]').forEach((section) => {
        const content = section.querySelector('[data-studio-lineup-content]');
        const button = section.querySelector('[data-studio-lineup-toggle]');
        const label = section.querySelector('[data-studio-lineup-toggle-label]');
        const icon = section.querySelector('[data-studio-lineup-toggle-icon]');

        content?.classList.toggle('hidden', collapsed);
        if (button) {
            button.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
            button.title = collapsed ? 'Zobrazit sestavy' : 'Skrýt sestavy';
        }
        if (label) label.textContent = collapsed ? 'Zobrazit' : 'Skrýt';
        icon?.classList.toggle('rotate-180', collapsed);
    });
}

function restoreStudioLineupState(center, root = center) {
    const collapsed = center.dataset.studioLineupCollapsed === '1';
    applyStudioLineupCollapsed(root, collapsed);
}

function setCommentFormBusy(form, busy) {
    if (!form) return;
    form.dataset.submitting = busy ? '1' : '0';
    form.classList.toggle('pointer-events-none', busy);
    form.classList.toggle('opacity-75', busy);

    const textarea = form.querySelector('[data-match-comment-textarea]');
    const button = form.querySelector('[data-match-comment-send]');
    const icon = form.querySelector('[data-match-comment-send-icon]');
    const spinner = form.querySelector('[data-match-comment-send-spinner]');

    if (textarea) textarea.readOnly = busy;
    if (button) button.disabled = busy;

    // Font Awesome sets its own display value on <i>. Toggle visibility on wrapper
    // elements as well as the native hidden property so the spinner cannot remain
    // visible after the AJAX request completes.
    if (icon) {
        icon.hidden = busy;
        icon.classList.toggle('hidden', busy);
        icon.classList.toggle('grid', !busy);
    }
    if (spinner) {
        spinner.hidden = !busy;
        spinner.classList.toggle('hidden', !busy);
        spinner.classList.toggle('grid', busy);
    }
}

function isNearBottom(feed) {
    if (!feed) return true;
    return feed.scrollHeight - feed.scrollTop - feed.clientHeight <= LIVE_BOTTOM_THRESHOLD;
}

function setPausedState(center, paused) {
    const manualOff = center.dataset.liveManual === '0';
    const moderationPaused = center.dataset.liveModerationPaused === '1';
    const connectionError = center.dataset.liveConnectionError === '1';
    center.dataset.liveScrollPaused = paused ? '1' : '0';
    const effective = Boolean(paused || manualOff || moderationPaused || connectionError);
    center.dataset.livePaused = effective ? '1' : '0';
    center.querySelectorAll('[data-live-paused]').forEach((el) => el.classList.toggle('hidden', !effective));
    center.querySelectorAll('[data-live-pause-message]').forEach((el) => {
        if (connectionError) {
            el.textContent = 'Spojení se Studiem se přerušilo. Automaticky zkouším obnovit živý přenos.';
        } else if (moderationPaused) {
            el.textContent = 'Live je dočasně pozastavené během úpravy zápasové události.';
        } else if (manualOff) {
            el.textContent = 'Live je vypnuté.';
        } else {
            el.textContent = 'Čteš starší zprávy. Live pozastaveno.';
        }
    });
    center.querySelectorAll('[data-live-toggle-label]').forEach((el) => {
        if (moderationPaused) el.textContent = 'Pokračovat';
        else el.textContent = manualOff ? 'Live vypnuto' : 'Live zapnuto';
    });
    center.querySelectorAll('[data-live-toggle]').forEach((button) => {
        const warning = Boolean(manualOff || moderationPaused || connectionError);
        button.classList.toggle('border-emerald-500/30', !warning);
        button.classList.toggle('bg-emerald-500/10', !warning);
        button.classList.toggle('text-emerald-300', !warning);
        button.classList.toggle('border-amber-500/35', warning);
        button.classList.toggle('bg-amber-500/10', warning);
        button.classList.toggle('text-amber-300', warning);
    });
}

function currentVisibleClockSeconds(center) {
    const clock = center.querySelector('[data-live-clock], [data-match-clock]');
    if (!clock) return -1;
    const current = Number(clock.dataset.currentSeconds);
    if (Number.isFinite(current) && current >= 0) return Math.floor(current);
    const source = Number(clock.dataset.seconds || 0);
    return Number.isFinite(source) ? Math.max(0, Math.floor(source)) : -1;
}

function setFormValue(form, name, value) {
    const control = form?.elements?.namedItem(name);
    if (!control) return;
    const nextValue = value ?? '';
    if (control.tomselect) {
        if (String(nextValue) === '') control.tomselect.clear(true);
        else control.tomselect.setValue(String(nextValue), true);
        control.dispatchEvent(new Event('change', { bubbles: true }));
        return;
    }
    control.value = nextValue;
    // A native radio list exposes value but dispatches change on the checked input.
    const target = typeof control.dispatchEvent === 'function'
        ? control
        : Array.from(control).find(input => input.checked);
    target?.dispatchEvent(new Event('change', { bubbles: true }));
}

function openStudioEventEditor(center, values = {}, { scroll = true } = {}) {
    if (!center) return;
    center.dataset.liveModerationPaused = '1';
    setPausedState(center, false);

    const form = center.querySelector('[data-event-editor]');
    if (!form) return;

    setFormValue(form, 'event_id', values.id ?? values.event_id ?? '0');
    setFormValue(form, 'sort_order', values.sort_order ?? '0');
    setFormValue(form, 'type', values.type ?? 'goal');
    setFormValue(form, 'team_id', values.team_id ?? '');
    setFormValue(form, 'minute', values.minute ?? '');
    setFormValue(form, 'extra_minute', values.extra_minute ?? '');
    setFormValue(form, 'primary_player_id', values.primary_player_id ?? '');
    setFormValue(form, 'secondary_player_id', values.secondary_player_id ?? '');
    setFormValue(form, 'note', values.note ?? '');
    form.querySelector('[data-event-edit-cancel]')?.classList.remove('hidden');

    if (scroll) form.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function clearStudioEventEditor(center, form, { releasePause = true } = {}) {
    if (!form) return;

    // Nepoužívej pouze form.reset(): po AJAX submitu mohou být právě odeslané
    // hodnoty novými HTML defaults. Vynuluj důležité položky explicitně.
    setFormValue(form, 'event_id', '0');
    setFormValue(form, 'sort_order', '0');
    setFormValue(form, 'type', 'goal');
    setFormValue(form, 'team_id', '');
    setFormValue(form, 'primary_player_id', '');
    setFormValue(form, 'secondary_player_id', '');
    setFormValue(form, 'minute', '');
    setFormValue(form, 'extra_minute', '');
    setFormValue(form, 'note', '');
    form.querySelector('[data-event-edit-cancel]')?.classList.add('hidden');

    if (releasePause && center) {
        center.dataset.liveModerationPaused = '0';
        const feed = center.querySelector('[data-match-live-feed]');
        setPausedState(center, feed ? !isNearBottom(feed) : false);
    }
}

function footballMinuteFromSeconds(seconds, phase) {
    const raw = Math.max(0, Math.floor(Number(seconds || 0) / 60));
    if ((phase === 'first_half' || phase === 'halftime') && raw > 45) return { minute: 45, extra: raw - 45 };
    if (phase === 'second_half' && raw > 90) return { minute: 90, extra: raw - 90 };
    if ((phase === 'extra_first' || phase === 'extra_break') && raw > 105) return { minute: 105, extra: raw - 105 };
    if (phase === 'extra_second' && raw > 120) return { minute: 120, extra: raw - 120 };
    return { minute: raw, extra: '' };
}

function applyLiveFilter(center) {
    const filter = center.dataset.liveFilter || 'all';
    center.querySelectorAll('[data-live-kind]').forEach((item) => {
        item.classList.toggle('hidden', filter !== 'all' && item.dataset.liveKind !== filter);
    });
    center.querySelectorAll('[data-live-filter]').forEach((item) => {
        const active = item.dataset.liveFilter === filter;
        item.classList.toggle('bg-sky-500/15', active);
        item.classList.toggle('text-sky-200', active);
        item.classList.toggle('ring-1', active);
        item.classList.toggle('ring-sky-400/25', active);
        item.classList.toggle('text-slate-400', !active);
    });
}

function withStreamLimit(url, center) {
    if (!url) return url;
    const feed = center.querySelector('[data-match-live-feed]');
    const limit = Number(feed?.dataset.streamLimit || 80);
    try {
        const parsed = new URL(url, window.location.href);
        parsed.searchParams.set('streamLimit', String(limit));
        return parsed.toString();
    } catch (_) {
        return url;
    }
}

function pinFeedToBottomDuringAnimation(feed, duration = LIVE_ITEM_ANIMATION_MS) {
    if (!feed) return;
    const startedAt = performance.now();
    const pin = (now) => {
        feed.scrollTop = feed.scrollHeight;
        if (now - startedAt < duration + 40) requestAnimationFrame(pin);
    };
    requestAnimationFrame(pin);
}

function animateInsertedItem(item, feed, pinToBottom = false) {
    if (!item || typeof item.animate !== 'function') {
        if (pinToBottom && feed) feed.scrollTop = feed.scrollHeight;
        return;
    }

    // The node is already in DOM so we can measure its real final height.
    const finalHeight = Math.max(1, item.getBoundingClientRect().height);
    item.style.overflow = 'hidden';

    const animation = item.animate(
        [
            { maxHeight: '0px', opacity: 0, transform: 'translateY(8px)' },
            { maxHeight: `${finalHeight}px`, opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: LIVE_ITEM_ANIMATION_MS, easing: 'cubic-bezier(.2,.8,.2,1)' },
    );

    if (pinToBottom) pinFeedToBottomDuringAnimation(feed);

    animation.addEventListener('finish', () => {
        item.style.overflow = '';
        item.style.maxHeight = '';
        item.style.opacity = '';
        item.style.transform = '';
        if (pinToBottom && feed) feed.scrollTop = feed.scrollHeight;
    }, { once: true });
}

function syncLoadOlderControl(currentFeed, incomingFeed) {
    const current = currentFeed.querySelector('[data-live-load-older-wrap]');
    const incoming = incomingFeed.querySelector('[data-live-load-older-wrap]');

    if (current && !incoming) {
        current.remove();
        return;
    }
    if (!current && incoming) {
        currentFeed.prepend(incoming.cloneNode(true));
        return;
    }
    if (current && incoming) current.replaceWith(incoming.cloneNode(true));
}


function currentLiveCursor(feed) {
    let lastCommentId = 0;
    let lastEventId = 0;
    feed?.querySelectorAll('[data-live-item]').forEach((node) => {
        const key = node.dataset.liveItem || '';
        if (key.startsWith('comment-')) lastCommentId = Math.max(lastCommentId, Number(key.slice(8)) || 0);
        if (key.startsWith('event-')) lastEventId = Math.max(lastEventId, Number(key.slice(6)) || 0);
    });
    return { lastCommentId, lastEventId };
}

function withLiveCursor(url, feed) {
    const target = new URL(url, window.location.href);
    const cursor = currentLiveCursor(feed);
    target.searchParams.set('lastCommentId', String(cursor.lastCommentId));
    target.searchParams.set('lastEventId', String(cursor.lastEventId));
    return target.toString();
}
function installLiveFeedMorph(naja) {
    if (!naja?.snippetHandler || naja.__clasicoLiveFeedMorphInstalled) return;
    naja.__clasicoLiveFeedMorphInstalled = true;

    naja.snippetHandler.addEventListener('beforeUpdate', (event) => {
        const snippet = event.detail?.snippet;
        const content = event.detail?.content;
        if (!snippet || snippet.id !== 'snippet--liveFeed' || typeof content !== 'string') return;

        const center = document.querySelector('[data-match-center]');
        const currentFeed = snippet.querySelector('[data-match-live-feed]');
        if (!center || !currentFeed) return;

        const holder = document.createElement('div');
        holder.innerHTML = content;
        const incomingFeed = holder.querySelector('[data-match-live-feed]');
        if (!incomingFeed) return;

        // Prevent Naja from replacing the whole scroll container. From this point we
        // morph only changed/new rows into the existing feed DOM.
        event.preventDefault();

        const wasBottom = isNearBottom(currentFeed) && center.dataset.livePaused !== '1';
        const beforeScrollTop = currentFeed.scrollTop;
        const beforeScrollHeight = currentFeed.scrollHeight;

        currentFeed.dataset.streamLimit = incomingFeed.dataset.streamLimit || currentFeed.dataset.streamLimit || '80';
        const deltaMode = incomingFeed.dataset.liveDelta === '1';
        if (!deltaMode) syncLoadOlderControl(currentFeed, incomingFeed);

        const currentItems = new Map(
            Array.from(currentFeed.querySelectorAll('[data-live-item]')).map((node) => [node.dataset.liveItem, node]),
        );
        const incomingItems = Array.from(incomingFeed.querySelectorAll('[data-live-item]'));
        const incomingIds = new Set(incomingItems.map((node) => node.dataset.liveItem));

        // Remove an empty-state placeholder once real rows arrive.
        if (incomingItems.length > 0) {
            Array.from(currentFeed.children).forEach((child) => {
                if (!child.matches?.('[data-live-item], [data-live-load-older-wrap]')) child.remove();
            });
        }

        const newlyInserted = [];

        incomingItems.forEach((incomingNode, index) => {
            const id = incomingNode.dataset.liveItem;
            const existing = currentItems.get(id);

            if (existing) {
                // Keep the physical row whenever possible. Replace only when its server
                // markup changed (e.g. reaction count), which has negligible scroll impact.
                if (existing.outerHTML !== incomingNode.outerHTML) {
                    const replacement = incomingNode.cloneNode(true);
                    existing.replaceWith(replacement);
                    currentItems.set(id, replacement);
                }
                return;
            }

            const clone = incomingNode.cloneNode(true);
            let nextExisting = null;
            for (let i = index + 1; i < incomingItems.length; i += 1) {
                const candidate = currentItems.get(incomingItems[i].dataset.liveItem);
                if (candidate?.isConnected) {
                    nextExisting = candidate;
                    break;
                }
            }

            if (nextExisting) currentFeed.insertBefore(clone, nextExisting);
            else currentFeed.appendChild(clone);

            currentItems.set(id, clone);
            newlyInserted.push({ node: clone, appendedAtEnd: !nextExisting });
        });

        // Full-window redraw may evict old rows. Delta polling never removes anything;
        // it only appends rows newer than the current cursor.
        if (!deltaMode) {
            currentItems.forEach((node, id) => {
                if (!incomingIds.has(id) && node.isConnected) node.remove();
            });
        }

        // Only a full-window response may replace the empty state. Delta polling with
        // zero new rows intentionally leaves the current feed untouched.
        if (!deltaMode && incomingItems.length === 0) {
            const empty = Array.from(incomingFeed.children).find((node) => !node.matches?.('[data-live-load-older-wrap]'));
            if (empty && !currentFeed.querySelector('[data-live-item]')) currentFeed.appendChild(empty.cloneNode(true));
        }

        applyLiveFilter(center);

        const appended = newlyInserted.filter((entry) => entry.appendedAtEnd);
        const prepended = newlyInserted.filter((entry) => !entry.appendedAtEnd);

        if (wasBottom) {
            currentFeed.scrollTop = currentFeed.scrollHeight;
            appended.forEach(({ node }) => animateInsertedItem(node, currentFeed, true));
            prepended.forEach(({ node }) => animateInsertedItem(node, currentFeed, false));
            setPausedState(center, false);
        } else {
            // Loading older content must keep the same visual row under the user's eyes.
            const heightDelta = currentFeed.scrollHeight - beforeScrollHeight;
            currentFeed.scrollTop = Math.max(0, beforeScrollTop + Math.max(0, heightDelta));
            prepended.forEach(({ node }) => animateInsertedItem(node, currentFeed, false));
            appended.forEach(({ node }) => animateInsertedItem(node, currentFeed, false));
            setPausedState(center, true);
        }
    });
}


function installStudioSidebarMorph(naja) {
    if (!naja?.snippetHandler || naja.__clasicoStudioSidebarMorphInstalled) return;
    naja.__clasicoStudioSidebarMorphInstalled = true;

    naja.snippetHandler.addEventListener('beforeUpdate', (event) => {
        const snippet = event.detail?.snippet;
        const content = event.detail?.content;
        if (!snippet || snippet.id !== 'snippet--matchSidebar' || typeof content !== 'string') return;

        const center = document.querySelector('[data-match-center]');
        if (!center) return;

        const holder = document.createElement('div');
        holder.innerHTML = content;
        const resolvedTab = applyStudioInfoTabToRoot(holder, center.dataset.studioInfoTab || '');
        if (resolvedTab) center.dataset.studioInfoTab = resolvedTab;
        restoreStudioLineupState(center, holder);

        // Při přepnutí z obou týmů zpět na hlavní tým může původně zvolená
        // záložka zmizet. V takovém případě automaticky použijeme jediný
        // dostupný panel (např. hostující Barcelonu), místo abychom ho skryli.
        event.preventDefault();
        snippet.innerHTML = holder.innerHTML;
    });
}

function applyStudioPollingState(center, state = null) {
    if (!center) return;

    if (state && typeof state === 'object') {
        if ('match' in state) center.dataset.matchRefreshEnabled = state.match ? '1' : '0';
        if ('discussion' in state) center.dataset.discussionRefreshEnabled = state.discussion ? '1' : '0';
        if ('presence' in state) center.dataset.presenceEnabled = state.presence ? '1' : '0';
        return;
    }

    const source = center.querySelector('[data-match-status-source]');
    if (!source) return;

    const status = source.dataset.matchStatusSource || '';
    const commentsOpen = source.dataset.commentsOpenSource === '1';
    const live = ['live', 'halftime', 'extra_time', 'penalties'].includes(status);
    center.dataset.matchRefreshEnabled = live ? '1' : '0';
    center.dataset.discussionRefreshEnabled = commentsOpen ? '1' : '0';
    center.dataset.presenceEnabled = live || commentsOpen ? '1' : '0';
}

function studioPresenceKey() {
    const storageKey = 'clasico-studio-presence-key';
    try {
        const stored = window.localStorage.getItem(storageKey);
        if (stored && /^[a-f0-9]{32,64}$/i.test(stored)) return stored.toLowerCase();

        let value = '';
        if (window.crypto?.getRandomValues) {
            const bytes = new Uint8Array(16);
            window.crypto.getRandomValues(bytes);
            value = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
        } else {
            value = `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`.padEnd(32, '0').slice(0, 32);
        }
        window.localStorage.setItem(storageKey, value);
        return value;
    } catch (_) {
        return `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`.padEnd(32, '0').slice(0, 32);
    }
}

function setStudioPresenceState(center, state) {
    if (!center) return;
    center.dataset.presenceState = state;
    center.querySelectorAll('[data-studio-presence-indicator]').forEach((element) => {
        element.classList.toggle('opacity-60', state === 'connecting');
        element.classList.toggle('text-rose-500', state === 'error');
        element.classList.toggle('dark:text-rose-400', state === 'error');
        element.classList.toggle('text-emerald-600', state !== 'error');
        element.classList.toggle('dark:text-emerald-400', state !== 'error');
        element.setAttribute('title', state === 'error'
            ? 'Počet online uživatelů se nepodařilo obnovit.'
            : 'Uživatelé se Studiem otevřeným během posledních 90 sekund');
    });
}

function installStudioPresence(center) {
    if (!center || center.dataset.presenceReady === '1' || !center.dataset.presenceUrl) return;
    center.dataset.presenceReady = '1';
    const key = studioPresenceKey();

    const ping = async () => {
        if (document.hidden || center.dataset.presenceEnabled !== '1') return;
        setStudioPresenceState(center, 'connecting');
        try {
            const url = new URL(center.dataset.presenceUrl, window.location.href);
            url.searchParams.set('presenceKey', key);
            const response = await fetch(url.toString(), {
                method: 'GET',
                credentials: 'same-origin',
                cache: 'no-store',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json',
                },
            });
            if (!response.ok) throw new Error(`Presence HTTP ${response.status}`);
            const payload = await response.json();
            const count = Number(payload?.activeStudioUsers);
            if (!Number.isFinite(count) || count < 0) throw new Error('Presence payload is invalid');

            center.querySelectorAll('[data-studio-active-users]').forEach((element) => {
                element.textContent = String(Math.floor(count));
            });
            setStudioPresenceState(center, 'online');
        } catch (_) {
            // Nezastavujeme Studio ani live feed; pouze vizuálně přiznáme, že
            // pomocný online čítač se nepodařilo obnovit. Další interval zkusí znovu.
            setStudioPresenceState(center, 'error');
        }
    };

    ping();
    window.setInterval(ping, 30000);
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) ping();
    });
    window.addEventListener('focus', ping);
}

function installMobileComposerDock(center) {
    if (!center || center.dataset.composerDockReady === '1') return;
    center.dataset.composerDockReady = '1';

    // Keep the composer in document flow, including while the iOS keyboard opens.
    const viewport = window.visualViewport;
    let frame = 0;
    const sync = () => {
        if (!center.isConnected) return;
        const feed = center.querySelector('[data-match-live-feed]');
        const wasAtBottom = feed && feed.scrollHeight - feed.clientHeight - feed.scrollTop < 40;
        const height = Math.round(viewport?.height || window.innerHeight);
        center.style.setProperty('--match-visible-height', height + 'px');
        if (wasAtBottom && feed) feed.scrollTop = feed.scrollHeight;
    };
    const schedule = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(sync);
    };
    viewport?.addEventListener('resize', schedule);
    window.addEventListener('resize', schedule);
    center.addEventListener('focusin', schedule);
    center.addEventListener('focusout', schedule);
    sync();
}

export function initMatchCenter(naja, root = document) {
    const center = document.querySelector('[data-match-center]');
    if (!center) return;

    installLiveFeedMorph(naja);
    installStudioSidebarMorph(naja);

    const phaseSource = center.querySelector('[data-match-phase-source]');
    if (phaseSource?.dataset.matchPhaseSource) {
        center.dataset.matchPhase = phaseSource.dataset.matchPhaseSource;
    }
    applyStudioPollingState(center);

    applyLiveFilter(center);
    applyStudioInfoTab(center);
    if (center.dataset.studioLineupCollapsed === undefined) {
        const lineupKey = `clasico-studio-lineup-${center.dataset.matchId || '0'}`;
        center.dataset.studioLineupStorageKey = lineupKey;
        try {
            center.dataset.studioLineupCollapsed = window.sessionStorage.getItem(lineupKey) === '1' ? '1' : '0';
        } catch (_) {
            center.dataset.studioLineupCollapsed = '0';
        }
    }
    restoreStudioLineupState(center);
    installStudioPresence(center);
    installMobileComposerDock(center);

    if (center.dataset.liveManual === undefined) {
        const key = `clasico-match-live-${center.dataset.matchId || '0'}`;
        center.dataset.liveStorageKey = key;
        center.dataset.liveManual = sessionStorage.getItem(key) === '0' ? '0' : '1';
        center.dataset.liveModerationPaused = '0';
    }

    const currentFeed = center.querySelector('[data-match-live-feed]');
    if (currentFeed && center.dataset.liveInitialised !== '1') {
        currentFeed.scrollTop = currentFeed.scrollHeight;
        center.dataset.liveInitialised = '1';
        setPausedState(center, false);
    }

    if (center.dataset.matchCenterReady !== '1') {
        center.dataset.matchCenterReady = '1';
        center.dataset.liveFilter = center.dataset.liveFilter || 'all';


        naja.addEventListener('start', (event) => {
            const element = event.detail?.element;
            const form = element?.matches?.('[data-match-comment-form]')
                ? element
                : element?.closest?.('[data-match-comment-form]');
            if (!form || !center.contains(form)) return;

            center.dataset.commentSubmitting = '1';
            center.__commentSubmitRequest = event.detail?.request || null;
            setCommentFormBusy(form, true);
        });

        naja.addEventListener('complete', () => {
            applyStudioPollingState(center);
        });

        naja.addEventListener('success', (event) => {
            const payload = event.detail?.payload || {};

            if (payload.studioPolling) {
                applyStudioPollingState(center, payload.studioPolling);
            }

            if (payload.studioEventSaved) {
                // moderatorTools už může být po snippet redrawi nový DOM. Ber vždy
                // aktuální formulář a pojisti jeho vyčištění i na klientovi.
                clearStudioEventEditor(center, center.querySelector('[data-event-editor]'));
            }

            if (payload.quickEventCreated && payload.quickEvent) {
                // Rychlá událost je už vidět v live feedu, ale moderátora rovnou
                // přesuneme do editoru té právě vytvořené položky pro doplnění hráčů.
                openStudioEventEditor(center, payload.quickEvent);
            }

            // Obsah komentářového pole čistí sdílená request-local obsluha
            // ve feed-ui.js; polling ani starší odpověď nesmějí smazat nový text.

        });

        naja.addEventListener('complete', (event) => {
            if (center.dataset.commentSubmitting !== '1') return;
            const activeRequest = center.__commentSubmitRequest;
            const completedRequest = event.detail?.request;
            if (activeRequest && completedRequest && activeRequest !== completedRequest) return;

            center.dataset.commentSubmitting = '0';
            center.__commentSubmitRequest = null;
            setCommentFormBusy(center.querySelector('[data-match-comment-form]'), false);
        });

        const refreshInFlight = { match: false, stream: false };
        const refresh = async (url, { stream = false } = {}) => {
            if (!url || document.hidden || center.dataset.commentSubmitting === '1') return;
            if (stream && center.dataset.discussionRefreshEnabled !== '1' && center.dataset.matchRefreshEnabled !== '1') return;
            if (!stream && center.dataset.matchRefreshEnabled !== '1') return;

            const requestKind = stream ? 'stream' : 'match';
            if (refreshInFlight[requestKind]) return;

            if (stream) {
                const feed = center.querySelector('[data-match-live-feed]');
                const scrollPaused = Boolean(feed && !isNearBottom(feed));
                if (center.dataset.liveManual === '0' || center.dataset.liveModerationPaused === '1' || scrollPaused) {
                    setPausedState(center, scrollPaused);
                    return;
                }
            }

            refreshInFlight[requestKind] = true;
            try {
                let requestUrl = url;
                if (stream) {
                    const feed = center.querySelector('[data-match-live-feed]');
                    requestUrl = withLiveCursor(withStreamLimit(url, center), feed);
                }
                await naja.makeRequest('GET', requestUrl, null, { history: false });
                if (stream) {
                    center.dataset.liveConnectionError = '0';
                    const feed = center.querySelector('[data-match-live-feed]');
                    setPausedState(center, feed ? !isNearBottom(feed) : false);
                }
            } catch (_) {
                if (stream) {
                    center.dataset.liveConnectionError = '1';
                    setPausedState(center, false);
                }
            } finally {
                refreshInFlight[requestKind] = false;
            }
        };

        const matchRefreshMs = Math.max(1000, Number(center.dataset.matchRefreshSeconds || 10) * 1000);
        const discussionRefreshMs = Math.max(1000, Number(center.dataset.discussionRefreshSeconds || 7) * 1000);

        window.setInterval(() => refresh(center.dataset.refreshUrl), matchRefreshMs);
        window.setInterval(() => refresh(center.dataset.discussionRefreshUrl, { stream: true }), discussionRefreshMs);

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) return;
            refresh(center.dataset.refreshUrl);
            refresh(center.dataset.discussionRefreshUrl, { stream: true });
        });
        window.addEventListener('online', () => {
            center.dataset.liveConnectionError = '0';
            refresh(center.dataset.refreshUrl);
            refresh(center.dataset.discussionRefreshUrl, { stream: true });
        });

        document.addEventListener('scroll', (event) => {
            const feed = event.target?.closest?.('[data-match-live-feed]');
            if (!feed || !center.contains(feed)) return;
            setPausedState(center, !isNearBottom(feed));
        }, true);

        document.addEventListener('click', async (event) => {
            const lineupToggle = event.target.closest('[data-studio-lineup-toggle]');
            if (lineupToggle && center.contains(lineupToggle)) {
                event.preventDefault();
                const collapsed = center.dataset.studioLineupCollapsed !== '1';
                center.dataset.studioLineupCollapsed = collapsed ? '1' : '0';
                if (center.dataset.studioLineupStorageKey) {
                    try {
                        window.sessionStorage.setItem(center.dataset.studioLineupStorageKey, center.dataset.studioLineupCollapsed);
                    } catch (_) {
                        // Soukromý režim / blokované úložiště nesmí rozbít samotné přepínání.
                    }
                }
                restoreStudioLineupState(center);
                return;
            }

            const studioTab = event.target.closest('[data-studio-tab]');
            if (studioTab && center.contains(studioTab)) {
                event.preventDefault();
                center.dataset.studioInfoTab = studioTab.dataset.studioTab || 'events';
                applyStudioInfoTab(center);
                return;
            }

            const liveToggle = event.target.closest('[data-live-toggle]');
            if (liveToggle && center.contains(liveToggle)) {
                event.preventDefault();
                if (center.dataset.liveModerationPaused === '1') {
                    center.dataset.liveModerationPaused = '0';
                    center.dataset.liveManual = '1';
                } else {
                    center.dataset.liveManual = center.dataset.liveManual === '0' ? '1' : '0';
                }
                if (center.dataset.liveStorageKey) sessionStorage.setItem(center.dataset.liveStorageKey, center.dataset.liveManual);
                const feed = center.querySelector('[data-match-live-feed]');
                setPausedState(center, feed ? !isNearBottom(feed) : false);
                if (center.dataset.liveManual === '1' && center.dataset.livePaused !== '1') {
                    refresh(center.dataset.discussionRefreshUrl, { stream: true });
                }
                return;
            }

            const liveResume = event.target.closest('[data-live-resume]');
            if (liveResume && center.contains(liveResume)) {
                event.preventDefault();
                center.dataset.liveManual = '1';
                center.dataset.liveModerationPaused = '0';
                center.dataset.liveConnectionError = '0';
                if (center.dataset.liveStorageKey) sessionStorage.setItem(center.dataset.liveStorageKey, '1');
                const feed = center.querySelector('[data-match-live-feed]');
                if (feed) feed.scrollTop = feed.scrollHeight;
                setPausedState(center, false);
                refresh(center.dataset.discussionRefreshUrl, { stream: true });
                return;
            }

            const phaseButton = event.target.closest('[data-studio-phase]');
            if (phaseButton && center.contains(phaseButton)) {
                event.preventDefault();
                event.stopPropagation();
                if (phaseButton.dataset.loading === '1') return;
                const question = phaseButton.dataset.confirm;
                if (question && !window.confirm(question)) return;

                phaseButton.dataset.loading = '1';
                phaseButton.classList.add('pointer-events-none', 'opacity-70');
                try {
                    const url = new URL(phaseButton.href, window.location.href);
                    const currentSeconds = currentVisibleClockSeconds(center);
                    const phaseAction = url.searchParams.get('phaseAction') || '';
                    // Start nové části má pevný fotbalový základ (0/45/90/105),
                    // takže do něj nesmíme přenést nastavení času z předchozího
                    // poločasu včetně nastavení. Při ukončení naopak pošleme přesně
                    // to, co moderátor právě vidí na lokálních plynulých hodinách.
                    if (currentSeconds >= 0 && ['first_half_end', 'extra_first_end', 'penalties_start', 'finish'].includes(phaseAction)) {
                        url.searchParams.set('clockSeconds', String(currentSeconds));
                    }
                    await naja.makeRequest('GET', url.toString(), null, { history: false });
                } catch (_) {
                } finally {
                    delete phaseButton.dataset.loading;
                    phaseButton.classList.remove('pointer-events-none', 'opacity-70');
                }
                return;
            }

            const moderationTrigger = event.target.closest('[data-live-moderation-trigger], [data-moderation-action]');
            if (moderationTrigger && center.contains(moderationTrigger)) {
                center.dataset.liveModerationPaused = '1';
                setPausedState(center, true);
            }

            const quickEvent = event.target.closest('[data-quick-team-event]');
            if (quickEvent && center.contains(quickEvent)) {
                event.preventDefault();
                if (quickEvent.dataset.loading === '1') return;
                quickEvent.dataset.loading = '1';
                try {
                    const url = new URL(quickEvent.dataset.eventUrl, window.location.href);
                    url.searchParams.set('clockSeconds', String(currentVisibleClockSeconds(center)));
                    await naja.makeRequest('GET', url.toString(), null, { history: false });
                } catch (_) {
                } finally {
                    delete quickEvent.dataset.loading;
                }
                return;
            }

            const createEvent = event.target.closest('[data-event-create]');
            if (createEvent && center.contains(createEvent)) {
                event.preventDefault();
                const t = footballMinuteFromSeconds(currentVisibleClockSeconds(center), center.dataset.matchPhase || 'pregame');
                openStudioEventEditor(center, {
                    id: 0,
                    type: createEvent.dataset.eventCreate || 'substitution',
                    team_id: createEvent.dataset.teamId || '',
                    minute: t.minute,
                    extra_minute: t.extra,
                });
                return;
            }

            const editEvent = event.target.closest('[data-event-edit]');
            if (editEvent && center.contains(editEvent)) {
                event.preventDefault();
                openStudioEventEditor(center, {
                    id: editEvent.dataset.eventId,
                    type: editEvent.dataset.eventType,
                    team_id: editEvent.dataset.eventTeam,
                    minute: editEvent.dataset.eventMinute,
                    extra_minute: editEvent.dataset.eventExtra,
                    primary_player_id: editEvent.dataset.eventPrimary,
                    secondary_player_id: editEvent.dataset.eventSecondary,
                    note: editEvent.dataset.eventNote,
                });
                return;
            }

            const clearEventEditor = event.target.closest('[data-event-editor-clear]');
            if (clearEventEditor && center.contains(clearEventEditor)) {
                event.preventDefault();
                clearStudioEventEditor(center, clearEventEditor.closest('form'));
                return;
            }

            const cancelEdit = event.target.closest('[data-event-edit-cancel]');
            if (cancelEdit && center.contains(cancelEdit)) {
                event.preventDefault();
                clearStudioEventEditor(center, cancelEdit.closest('form'));
                return;
            }

            const older = event.target.closest('[data-live-load-older]');
            if (older && center.contains(older)) {
                event.preventDefault();
                event.stopPropagation();

                const feed = center.querySelector('[data-match-live-feed]');
                const beforeTop = feed?.scrollTop || 0;
                const beforeHeight = feed?.scrollHeight || 0;
                setPausedState(center, true);

                try {
                    await naja.makeRequest('GET', older.href, null, { history: false });
                    const current = center.querySelector('[data-match-live-feed]');
                    if (current) current.scrollTop = beforeTop + Math.max(0, current.scrollHeight - beforeHeight);
                } catch (_) {}
                return;
            }

            const button = event.target.closest('[data-live-filter]');
            if (!button || !center.contains(button)) return;
            center.dataset.liveFilter = button.dataset.liveFilter || 'all';
            applyLiveFilter(center);
        }, true);

        document.addEventListener('keydown', (event) => {
            const textarea = event.target.closest?.('[data-match-comment-textarea]');
            if (!textarea || !center.contains(textarea)) return;
            if (event.key !== 'Enter' || event.shiftKey || event.ctrlKey || event.metaKey || event.isComposing) return;
            event.preventDefault();
            const form = textarea.closest('form');
            if (form && !textarea.value.trim()) return;
            form?.requestSubmit();
        }, true);
    }

}
